import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  BASIC_CATEGORY_INDEX,
  PACKAGE_CATEGORIES,
  SOLO_CATEGORY_INDEX,
  SOLO_UPGRADE_CATEGORY_INDEX,
  UPGRADE_CATEGORY_INDEX,
} from './packageCategories';
import { defaultRoomPricing, deriveExtraNightSell, newPackageOptionId } from './packageOptionsModel';
import {
  DEFAULT_OCCUPANCY,
  type DealOptionGroup,
  type PackageRole,
  type PackageRoleChoices,
  occupancyOf,
  packageKey,
} from './previousPackageVariants';
import {
  type CategoryRoom,
  type OptionState,
  type RoomAssetLink,
  type RoomPricing,
  type UpliftType,
  roomVariantKey,
} from './types';

export type PreviousPackage = {
  asset: RoomAssetLink;
  categoryIndex: number;
  suffix?: string;
  upliftType?: UpliftType;
  dealIdByLos: Record<number, string>;
  occupancy: string;
  allocation: string;
  sellByLos: Record<number, number>;
  netByLos: Record<number, number>;
  extraNightSell?: number;
  extraNightNet?: number;
};

export type PreviousDealOptions = {
  packages: Array<PreviousPackage>;
  ambiguousCells: number;
  mixedOccupancyPackages: number;
  otherCurrencyNetCells: number;
};

type CopiedRole = Exclude<PackageRole, 'skip'>;

type MergedPackage = {
  asset: Required<RoomAssetLink>;
  role: CopiedRole;
  suffix: string;
  bundled: boolean;
  dealsByLos: Record<number, Array<OpportunityRelatedDeal>>;
};

const CATEGORY_BY_ROLE: Record<CopiedRole, number> = {
  base: BASIC_CATEGORY_INDEX,
  upgrade: UPGRADE_CATEGORY_INDEX,
  solo: SOLO_CATEGORY_INDEX,
  soloUpgrade: SOLO_UPGRADE_CATEGORY_INDEX,
};

// The grid holds one occupancy per row, so a row whose Deal Options name different ones keeps the default.
function packageOccupancy(deals: Array<OpportunityRelatedDeal>): string | undefined {
  const occupancies = new Set(deals.map((deal) => occupancyOf(deal.name) ?? DEFAULT_OCCUPANCY));
  return occupancies.size === 1 ? [...occupancies][0] : undefined;
}

function positiveNumber(value: unknown): number | undefined {
  const number = Number(value);
  return number > 0 ? number : undefined;
}

function mergeByRoleAndSuffix(groups: Array<DealOptionGroup>, choices: PackageRoleChoices): Array<MergedPackage> {
  const packagesByKey = new Map<string, MergedPackage>();

  for (const group of groups) {
    const choice = choices[group.key];
    if (!choice || choice.role === 'skip') {
      continue;
    }
    const suffix = choice.role === 'base' ? '' : choice.suffix.trim();
    const key = packageKey(group.asset.id, choice);
    const merged = packagesByKey.get(key) ?? {
      asset: group.asset,
      role: choice.role,
      suffix,
      bundled: false,
      dealsByLos: {},
    };
    merged.bundled = merged.bundled || group.bundled;
    for (const [nights, deals] of Object.entries(group.dealsByLos)) {
      merged.dealsByLos[Number(nights)] = [...(merged.dealsByLos[Number(nights)] ?? []), ...deals];
    }
    packagesByKey.set(key, merged);
  }

  return [...packagesByKey.values()];
}

// Salesforce keeps sell in AUD but cost in the Deal Option's own currency, so net is only copied when
// that currency is the one this offer's net prices are entered in.
function toPreviousPackage(
  { asset, role, suffix, bundled }: MergedPackage,
  occupancy: string,
  dealByLos: Record<number, OpportunityRelatedDeal>,
  netCurrency: string,
): PreviousPackage {
  const lengthsOfStay = Object.keys(dealByLos).map(Number);
  const dealIdByLos: Record<number, string> = {};
  const sellByLos: Record<number, number> = {};
  const netByLos: Record<number, number> = {};
  const netOf = (deal: OpportunityRelatedDeal, field: 'cost_price__c' | 'nightly_cost_price__c') => {
    const net = deal.currencyisocode === netCurrency ? positiveNumber(deal[field]) : undefined;
    return net === undefined ? undefined : Math.round(net);
  };

  for (const nights of lengthsOfStay) {
    const deal = dealByLos[nights];
    dealIdByLos[nights] = String(deal.id);
    sellByLos[nights] = Number(deal.price__c);
    const net = netOf(deal, 'cost_price__c');
    if (net !== undefined) {
      netByLos[nights] = net;
    }
  }

  // The tool holds one extra night price per row; it derives it from the shortest stay, so copy that one's.
  const shortestStayDeal = dealByLos[Math.min(...lengthsOfStay)];

  return {
    asset,
    categoryIndex: CATEGORY_BY_ROLE[role],
    ...(suffix ? { suffix } : {}),
    upliftType: role === 'upgrade' ? (bundled ? 'bundled_experience' : 'property_net') : undefined,
    dealIdByLos,
    occupancy,
    // Package Options shows the Base row's allocation on every row of that room.
    allocation: role === 'base' ? String(shortestStayDeal.allocation__c ?? '').trim() : '',
    sellByLos,
    netByLos,
    extraNightSell: positiveNumber(shortestStayDeal.nightly_sell_price__c),
    extraNightNet: netOf(shortestStayDeal, 'nightly_cost_price__c'),
  };
}

// Every copied cell comes from exactly one Deal Option: a stay length two merged groups both have is left out.
export function reducePreviousDealOptions(
  groups: Array<DealOptionGroup>,
  choices: PackageRoleChoices,
  netCurrency: string,
): PreviousDealOptions {
  const packages: Array<PreviousPackage> = [];
  let ambiguousCells = 0;
  let mixedOccupancyPackages = 0;
  let otherCurrencyNetCells = 0;

  for (const merged of mergeByRoleAndSuffix(groups, choices)) {
    const dealByLos: Record<number, OpportunityRelatedDeal> = {};
    for (const [nights, cellDeals] of Object.entries(merged.dealsByLos)) {
      if (cellDeals.length === 1) {
        dealByLos[Number(nights)] = cellDeals[0];
      } else {
        ambiguousCells += 1;
      }
    }
    if (Object.keys(dealByLos).length === 0) {
      continue;
    }
    const categoryOccupancy = PACKAGE_CATEGORIES[CATEGORY_BY_ROLE[merged.role]].defaultOccupancy;
    const isSoloRole = merged.role === 'solo' || merged.role === 'soloUpgrade';
    const occupancy = isSoloRole ? categoryOccupancy : packageOccupancy(Object.values(dealByLos));
    if (occupancy === undefined) {
      mixedOccupancyPackages += 1;
    }
    otherCurrencyNetCells += Object.values(dealByLos).filter(
      (deal) => deal.currencyisocode !== netCurrency && positiveNumber(deal.cost_price__c) !== undefined,
    ).length;
    packages.push(toPreviousPackage(merged, occupancy ?? categoryOccupancy, dealByLos, netCurrency));
  }

  return { packages, ambiguousCells, mixedOccupancyPackages, otherCurrencyNetCells };
}

export function previousLengthsOfStay({ packages }: PreviousDealOptions): Array<number> {
  return [...new Set(packages.flatMap((previous) => Object.keys(previous.sellByLos).map(Number)))].sort(
    (a, b) => a - b,
  );
}

function copiedRoomPricing(previous: PreviousPackage, lengthsOfStay: Array<number>): RoomPricing {
  const pricing: RoomPricing = {
    ...defaultRoomPricing(previous.occupancy),
    allocation: previous.allocation,
    extraNightNet: previous.extraNightNet === undefined ? '' : String(previous.extraNightNet),
  };

  for (const nights of lengthsOfStay) {
    if (previous.sellByLos[nights] !== undefined) {
      pricing.sellByLos[nights] = String(previous.sellByLos[nights]);
      pricing.sellEditedByLos[nights] = true;
    }
    if (previous.netByLos[nights] !== undefined) {
      pricing.netByLos[nights] = String(previous.netByLos[nights]);
    }
  }

  if (previous.extraNightSell !== undefined) {
    return { ...pricing, extraNightSell: String(previous.extraNightSell), extraNightSellEdited: true };
  }
  return { ...pricing, extraNightSell: deriveExtraNightSell(pricing, lengthsOfStay) };
}

// A row already there for the same package keeps its id, so inclusions applied to it stay attached.
function existingRoomId(state: OptionState, { asset, categoryIndex, suffix }: PreviousPackage) {
  const variantKey = roomVariantKey({ assetId: asset.id, suffix });
  return state.roomsByCategory[categoryIndex]?.find((room) =>
    categoryIndex === BASIC_CATEGORY_INDEX ? room.assetId === asset.id : roomVariantKey(room) === variantKey,
  )?.id;
}

export function replaceWithPreviousPackages(state: OptionState, previous: PreviousDealOptions): OptionState {
  const lengthsOfStay = previousLengthsOfStay(previous);
  const roomsByCategory: Array<Array<CategoryRoom>> = PACKAGE_CATEGORIES.map(() => []);
  const pricingByRoomId: Record<string, RoomPricing> = {};

  for (const previousPackage of previous.packages) {
    const { asset, categoryIndex, suffix, upliftType } = previousPackage;
    const room: CategoryRoom = {
      id: existingRoomId(state, previousPackage) ?? newPackageOptionId(),
      assetId: asset.id,
      assetName: asset.name,
      ...(suffix ? { suffix } : {}),
      asiaOnly: false,
      ...(upliftType ? { upliftType } : {}),
    };

    roomsByCategory[categoryIndex].push(room);
    pricingByRoomId[room.id] = copiedRoomPricing(previousPackage, lengthsOfStay);
  }

  return {
    ...state,
    lengthsOfStay,
    pricingByRoomId,
    roomsByCategory,
    netRateFormula: { ...state.netRateFormula, selectedRoomIds: [] },
  };
}
